// REACT & REACT HOOKS
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { FormattedMessage, useIntl } from "react-intl";

// STORE ACTIONS
import { getAllEmployees, deleteEmployee } from "store/actions";

// COMPONENTS
import ControlArea from "components/Layout/control-area";
import BootyPagination from "components/shared/pagination";
import TableControl from "components/shared/table";
import Input from "components/Form/input";
import { Dropdown, Modal } from "react-bootstrap";

// ICONS & ASSETS
import Icon from "./assets/employee.svg";
import { ReactComponent as MoreIcon } from "./assets/more.svg";
import { ReactComponent as EditIcon } from "./assets/edit.svg";
import { ReactComponent as DeleteIcon } from "./assets/delete.svg";

// HELPERS
import { handleErrMsg, getId } from "helpers/functions";
import { toastErrorMessage } from "helpers/toaster/toastErrorMessage";

const Employees = () => {
    const { employees, loading, error, singleEmployee } = useSelector(
        (state) => state.employees
    );
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { formatMessage, locale } = useIntl();
    const [isDispatched, setIsDispatched] = useState(false);
    const [searchParams] = useSearchParams();

    const [closeToggle, setCloseToggle] = useState(true);
    const handleClose = () => setCloseToggle(!closeToggle);

    useEffect(() => {
        setIsDispatched(true);
        dispatch(getAllEmployees(searchParams));
    }, [dispatch, searchParams]);

    // error
    useEffect(() => {
        if (error && isDispatched) {
            toastErrorMessage(
                formatMessage({ id: handleErrMsg(error) }),
                locale
            );
        }
    }, [error, formatMessage, locale, isDispatched]);

    const renderInvitationLinkModal = (
        <Modal
            show={
                singleEmployee?.invitationLink &&
                singleEmployee?.isNew &&
                closeToggle
            }
            onHide={handleClose}
        >
            <Modal.Header closeButton>
                <h5>
                    <FormattedMessage id="invitationLink" />
                </h5>
            </Modal.Header>
            <Modal.Body>
                <Input
                    type="url"
                    value={singleEmployee?.invitationLink}
                    copyable="true"
                    readOnly="true"
                    lg="12"
                />
            </Modal.Body>
        </Modal>
    );

    const handleEditTheme = (id) => {
        navigate(`/employees/${id}`);
    };

    const handleDeleteTheme = (id) => {
        dispatch(deleteEmployee(id));
    };

    const columns = [
        {
            name: <FormattedMessage id="firstName" />,
            selector: (row) => row?.firstName,
            sortable: true,
        },
        {
            name: <FormattedMessage id="lastName" />,
            selector: (row) => row?.lastName,
            sortable: true,
        },
        {
            name: <FormattedMessage id="email" />,
            selector: (row) => row?.email,
            sortable: true,
        },
        {
            name: <FormattedMessage id="role" />,
            selector: (row) =>
                formatMessage({
                    id:
                        row?.role?.name?.[locale] ||
                        row?.role?.name ||
                        row?.role,
                }),
            sortable: true,
        },
        {
            name: <FormattedMessage id="status" />,
            selector: (row) =>
                formatMessage({
                    id: row?.isVerified || " ",
                }),
            sortable: true,
        },

        {
            cell: (row) => (
                <>
                    {
                        <div className="global-dropdown">
                            <Dropdown>
                                <Dropdown.Toggle>
                                    <MoreIcon />
                                </Dropdown.Toggle>

                                <Dropdown.Menu>
                                    {
                                        <Dropdown.Item
                                            onClick={() =>
                                                handleEditTheme(getId(row))
                                            }
                                        >
                                            <EditIcon />
                                            <FormattedMessage id={"edit"} />
                                        </Dropdown.Item>
                                    }

                                    {
                                        <Dropdown.Item
                                            onClick={() =>
                                                handleDeleteTheme(getId(row))
                                            }
                                        >
                                            <DeleteIcon />
                                            <FormattedMessage id={"delete"} />
                                        </Dropdown.Item>
                                    }
                                </Dropdown.Menu>
                            </Dropdown>
                        </div>
                    }
                </>
            ),
            allowOverflow: true,
            button: true,
            width: "56px",
        },
    ];
    return (
        <div className="applications-wrap">
            <ControlArea
                btnTxt={<FormattedMessage id={"addEmployee"} />}
                cardTxt={<FormattedMessage id={"employees"} />}
                icon={Icon}
                url="/employees/add"
            />
            <TableControl
                title={"employeesTableShowAll"}
                columns={columns}
                data={employees}
                loading={loading}
            />
            <BootyPagination metaData={employees?.metadata} />
            {renderInvitationLinkModal}
        </div>
    );
};

export default Employees;
